/** Whether a write reached localStorage. */
type StorageWrite = 'written' | 'failed';

const lastWrites = new Map<string, StorageWrite>();

/**
 * Writes `value` under `name` and says whether it got there. A full quota,
 * private browsing or a blocked origin makes `setItem` throw; that is
 * reported, never thrown.
 */
export function writeStorage(name: string, value: string): StorageWrite {
  let result: StorageWrite;
  try {
    window.localStorage.setItem(name, value);
    result = 'written';
  } catch {
    result = 'failed';
  }
  lastWrites.set(name, result);
  return result;
}

/** What the last write under `name` from this page did, if any. */
export function lastStorageWrite(name: string): StorageWrite | undefined {
  return lastWrites.get(name);
}

/** Removes `name`, and says whether storage took the removal. */
export function removeStorage(name: string): StorageWrite {
  let result: StorageWrite;
  try {
    window.localStorage.removeItem(name);
    result = 'written';
  } catch {
    result = 'failed';
  }
  lastWrites.set(name, result);
  return result;
}

/** The names stored under `prefix`, or none when storage cannot be read. */
export function storageNames(prefix: string): string[] {
  try {
    const storage = window.localStorage;
    const names: string[] = [];
    for (let index = 0; index < storage.length; index++) {
      const name = storage.key(index);
      if (name?.startsWith(prefix)) names.push(name);
    }
    return names;
  } catch {
    return [];
  }
}

/** Reads `name`, or null when nothing is there or storage cannot be read. */
export function readStorage(name: string): string | null {
  try {
    return window.localStorage.getItem(name);
  } catch {
    return null;
  }
}
